import React, { useState } from "react";
import { useSelector } from "react-redux";
import { IStore } from "@/redux/defaultStore";
import { QuestConfigMilestoneResponse } from "@devour/client";
import { useRedeemCode } from "@/hooks/useRedeemCode";
import {Modal} from "@/widgets/Modal";
import { FaX } from "react-icons/fa6";

interface CodeEntryModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    milestone: QuestConfigMilestoneResponse;
    questConfigId: string;
    /** Custom class name prefix for styling */
    classNamePrefix?: string;
    /** CSS variables for theming (from useChallengeTheme hook) */
    themeStyle?: React.CSSProperties;
}

/**
 * Modal for entering promo codes to complete CODE_ENTRY milestones
 *
 * Used for bottle cap codes, campaign codes, and other pre-generated promo codes
 * that brands upload for campaign activations.
 */
export const CodeEntryModal: React.FC<CodeEntryModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    milestone,
    questConfigId,
    classNamePrefix = "code-entry-modal",
    themeStyle,
}) => {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const [code, setCode] = useState("");
    const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
    const [errorMessage, setErrorMessage] = useState<string>("");

    const redeemCodeMutation = useRedeemCode(fullToken);

    // Reset state when modal opens
    React.useEffect(() => {
        if (isOpen) {
            setCode("");
            setStatus("idle");
            setErrorMessage("");
        }
    }, [isOpen]);

    const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Auto-uppercase and strip whitespace as user types
        setCode(e.target.value.toUpperCase().replace(/\s/g, ""));
        if (status === "error") {
            setStatus("idle");
            setErrorMessage("");
        }
    };

    const handleSubmit = async () => {
        if (!code.trim() || !milestone.id || redeemCodeMutation.isPending) return;

        try {
            await redeemCodeMutation.mutateAsync({
                code: code.trim(),
                questConfigId,
                milestoneId: milestone.id,
            });
            setStatus("success");
            onSuccess?.();
        } catch (err: any) {
            setStatus("error");
            setErrorMessage(err.message || "Invalid code. Please try again.");
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" && code.trim()) {
            handleSubmit();
        }
    };

    // Get instructions from milestone or use default
    const instructions = (milestone as any)?.codeInstructions ||
        "Enter your promo code below to complete this milestone.";

    return (
        <Modal
            isOpen={isOpen}
            toggle={onClose}
            size="xs"
            contentClassName={`${classNamePrefix}-container`}
        >
            <div className={classNamePrefix} style={themeStyle}>
                <div
                    role="button"
                    tabIndex={0}
                    className={`${classNamePrefix}_close-button-row`}
                    onClick={onClose}
                    onKeyDown={(e) => e.key === "Enter" && onClose()}
                >
                    <FaX size={16} />
                </div>

                <div className={`${classNamePrefix}_header`}>
                    <div className={`${classNamePrefix}_title`}>
                        {milestone.name || "ENTER CODE"}
                    </div>
                    {milestone.description &&
                        <div className={`${classNamePrefix}_subtitle`}>
                            {milestone.description}
                        </div>
                    }
                </div>

                {status !== "success" &&
                    <div className={`${classNamePrefix}_instructions`}>
                        {instructions}
                    </div>
                }

                {status !== "success" &&
                    <div className={`${classNamePrefix}_input-container`}>
                        <input
                            type="text"
                            className={`${classNamePrefix}_input ${status === "error" ? "error" : ""}`}
                            placeholder="Enter code..."
                            value={code}
                            onChange={handleCodeChange}
                            onKeyPress={handleKeyPress}
                            disabled={redeemCodeMutation.isPending}
                            autoCapitalize="characters"
                            autoCorrect="off"
                            spellCheck="false"
                        />
                    </div>
                }

                {status === "success" &&
                    <div className={`${classNamePrefix}_success`}>
                        <div className={`${classNamePrefix}_success_icon`}>
                            <span>✓</span>
                        </div>
                        <div className={`${classNamePrefix}_success_title`}>
                            CODE ACCEPTED!
                        </div>
                        <div className={`${classNamePrefix}_success_text`}>
                            Your code has been verified and the milestone is complete!
                        </div>
                    </div>
                }

                {status === "error" &&
                    <div className={`${classNamePrefix}_error`}>
                        <span className={`${classNamePrefix}_error_oops`}>Oops!</span>
                        {" "}{errorMessage}
                    </div>
                }

                <div className={`${classNamePrefix}_button-row`}>
                    {status !== "success" &&
                        <button
                            onClick={handleSubmit}
                            className={`${classNamePrefix}_submit-button`}
                            disabled={!code.trim() || redeemCodeMutation.isPending}
                        >
                            {redeemCodeMutation.isPending ? "Verifying..." : "Submit Code"}
                        </button>
                    }
                    <button
                        onClick={onClose}
                        className={`${classNamePrefix}_close-button`}
                    >
                        {status === "success" ? "Done" : "Close"}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default CodeEntryModal;
